// ============================================================================
// CLEO'S SECRET — InvitationBlock
// File: src/components/marketing/InvitationBlock.jsx
//
// The platform-wide closing-section invitation pattern. Extracted as a reusable
// atom because the exact same block (eyebrow → roman+italic title → italic
// subtitle → primary CTA) appears in all 6 marketing copy docs (home, about,
// approach, membership, stories, journal). Default copy matches the
// doc-locked invitation; pages with copy variants (e.g. home-page-copy's
// slightly different subtitle phrasing) pass overrides as props.
//
// Used inside a parent <section> that supplies the page-level padding and
// background. This atom owns only the inner editorial block, not the section
// container.
//
// Backlog (Session 1 close): StoriesPage and AnimatedHero currently inline a
// near-identical block. Refactor to import this atom in a later session.
//
// Props:
//   onDark        boolean   true → OBS background register (use on dark
//                           sections; flips colour palette to GO2/IVY/PAP)
//   eyebrowText   string    default "WHEN YOU'RE READY"
//   titleRoman    string    default "Begin your"
//   titleItalic   string    default "health conversation."
//   subtitle      ReactNode default 2-line doc-locked subtitle; override to
//                           supply a page-specific variant
//   ctaLabel      string    default "Begin your health conversation"
//   onCtaClick    fn        click handler
// ============================================================================

import React from 'react';
import {
  P, T,
  CONTAINER_READING,
  Eyebrow, SectionDivider, SectionHeadline, BtnPrimary,
} from '../../lib/design-tokens';

export default function InvitationBlock({
  onDark,
  eyebrowText,
  titleRoman,
  titleItalic,
  subtitle,
  ctaLabel,
  onCtaClick,
}) {
  var isDark = onDark === true;
  var eyebrowLabel = eyebrowText || "WHEN YOU'RE READY";
  var roman = titleRoman || 'Begin your';
  var italic = titleItalic || 'health conversation.';
  var ctaText = ctaLabel || 'Begin your health conversation';
  var defaultSubtitle = (
    <React.Fragment>
      <span style={{ display: 'block' }}>
        There&apos;s no rush, and no triage. A few considered questions, in your own time.
      </span>
      <span style={{ display: 'block', marginTop: 6 }}>
        We&apos;ll take it from there.
      </span>
    </React.Fragment>
  );

  return (
    <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
      <SectionDivider onDark={isDark} />
      <div style={{ marginTop: 'clamp(40px, 4vw, 56px)', marginBottom: 28 }}>
        <Eyebrow center color={isDark ? P.GO2 : P.BR2}>{eyebrowLabel}</Eyebrow>
      </div>
      <SectionHeadline roman={roman} italic={italic} onDark={isDark} />
      <div style={{ maxWidth: 640, margin: '0 auto clamp(40px, 4vw, 56px)' }}>
        <p style={{
          ...T.subtitle,
          color: isDark ? P.GO2 : P.BR2,
        }}>{subtitle || defaultSubtitle}</p>
      </div>
      <BtnPrimary onClick={onCtaClick} onDark={isDark} large>{ctaText}</BtnPrimary>
    </div>
  );
}
